import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Upload, Download, Play, Film, FileText, Image, Package, History, Globe, X, Mic, Linkedin, MessageCircle, Volume2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const downloadWithProgress = async (url, filename, onProgress) => {
  const response = await api.get(url, {
    responseType: 'blob',
    onDownloadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export default function InstitutionalVideosPage() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [preview, setPreview] = useState(null);
  const [versionsOf, setVersionsOf] = useState(null);
  const [voice, setVoice] = useState('onyx');
  const [generating, setGenerating] = useState(false);
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/videos');
      setData(data);
    } catch (e) { toast.error('Error al cargar los videos'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (slot, file) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/videos/${slot}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Video subido (v${data.version}, ${data.size_mb} MB)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al subir el video'); }
    finally { setUploading(null); }
  };

  const download = async (slot, filename) => {
    setDownloading(prev => ({ ...prev, [slot]: 0 }));
    try {
      await downloadWithProgress(`/videos/${slot}/download`, filename,
        (p) => setDownloading(prev => ({ ...prev, [slot]: p })));
      toast.success('Descarga completada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error en la descarga'); }
    finally { setDownloading(prev => { const n = { ...prev }; delete n[slot]; return n; }); }
  };

  const downloadAsset = async (key, filename) => {
    try { await downloadWithProgress(`/videos/assets/${key}`, filename, () => {}); }
    catch (e) { toast.error(e.response?.data?.detail || 'Recurso no disponible'); }
  };

  const setAsLandingVideo = async (slot) => {
    try {
      const { data } = await api.post(`/videos/${slot}/use-in-landing`);
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const togglePublic = async (slot, value) => {
    try {
      await api.put(`/videos/${slot}/config`, { public_download: value });
      toast.success(value ? 'Descarga pública activada' : 'Descarga pública desactivada');
      load();
    } catch (e) { toast.error('Error'); }
  };

  const restore = async (slot, version) => {
    try {
      const { data } = await api.post(`/videos/${slot}/restore/${version}`);
      toast.success(data.message);
      setVersionsOf(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al restaurar'); }
  };

  if (!data) return <Loader2 className="animate-spin mx-auto my-12" />;

  return (
    <div className="space-y-6" data-testid="institutional-videos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Videos Institucionales</h1>
          <p className="text-sm text-slate-500">Gestiona los videos comerciales de SimuClinic: streaming, descargas, versiones y Media Kit.</p>
        </div>
        <Button onClick={async () => {
          try {
            const r = await api.get('/videos/media-kit', { responseType: 'blob' });
            const u = window.URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement('a'); a.href = u; a.download = 'SimuClinic_Media_Kit.zip';
            document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u);
            toast.success('Media Kit descargado');
          } catch (e) { toast.error('No hay archivos disponibles aún'); }
        }} className="gap-2" data-testid="download-mediakit-btn">
          <Package size={16} /> Descargar Media Kit
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {data.videos.map(v => (
          <Card key={v.slot} data-testid={`video-card-${v.slot}`}>
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <Film size={18} className="text-cyan-600" />
                  <CardTitle className="text-base">{v.nombre}</CardTitle>
                </div>
                <Badge className={v.status === 'disponible' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}
                  data-testid={`video-status-${v.slot}`}>
                  {v.status === 'disponible' ? 'Disponible' : 'Pendiente'}
                </Badge>
              </div>
              <CardDescription className="text-xs">
                {v.descripcion} · {v.resolucion} · {v.formato.toUpperCase()}
                {v.size_mb ? ` · ${v.size_mb} MB` : ''} {v.version ? ` · v${v.version}` : ''}
                {v.uploaded_at ? ` · ${new Date(v.uploaded_at).toLocaleDateString('es-CL')}` : ''}
              </CardDescription>
              <p className="text-[11px] text-slate-400 font-mono">{v.filename}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={v.status !== 'disponible'}
                  onClick={() => setPreview(v)} data-testid={`view-btn-${v.slot}`}>
                  <Play size={14} className="mr-1" /> Ver
                </Button>
                <Button size="sm" disabled={v.status !== 'disponible' || downloading[v.slot] !== undefined}
                  onClick={() => download(v.slot, v.filename)} data-testid={`download-btn-${v.slot}`}>
                  {downloading[v.slot] !== undefined
                    ? <><Loader2 size={14} className="animate-spin mr-1" /> {downloading[v.slot]}%</>
                    : <><Download size={14} className="mr-1" /> Descargar</>}
                </Button>
                <input type="file" accept=".mp4,.webm" className="hidden"
                  ref={el => (fileRefs.current[v.slot] = el)}
                  onChange={e => upload(v.slot, e.target.files?.[0])} data-testid={`upload-input-${v.slot}`} />
                <Button size="sm" variant="outline" disabled={uploading === v.slot}
                  onClick={() => fileRefs.current[v.slot]?.click()} data-testid={`upload-btn-${v.slot}`}>
                  {uploading === v.slot ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />}
                  {v.status === 'disponible' ? 'Reemplazar' : 'Subir video'}
                </Button>
                {v.slot === 'video_30s' && (
                  <Button size="sm" variant="outline" disabled={v.status !== 'disponible'}
                    onClick={() => setAsLandingVideo(v.slot)} data-testid={`use-landing-btn-${v.slot}`}>
                    <Globe size={14} className="mr-1" /> Usar en landing
                  </Button>
                )}
                {v.slot === 'video_30s' && v.status === 'disponible' && (
                  <>
                    <Button size="sm" variant="outline" className="text-[#0A66C2] border-[#0A66C2]/40 hover:bg-[#0A66C2]/10"
                      onClick={() => {
                        const url = encodeURIComponent(`${window.location.origin}/#demo`);
                        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'noopener,width=600,height=600');
                      }} data-testid="share-linkedin-btn">
                      <Linkedin size={14} className="mr-1" /> LinkedIn
                    </Button>
                    <Button size="sm" variant="outline" className="text-[#25D366] border-[#25D366]/40 hover:bg-[#25D366]/10"
                      onClick={() => {
                        const msg = encodeURIComponent(`Conoce SimuClinic en 30 segundos — simulación clínica digital para la formación en salud: ${window.location.origin}/#demo`);
                        window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
                      }} data-testid="share-whatsapp-btn">
                      <MessageCircle size={14} className="mr-1" /> WhatsApp
                    </Button>
                  </>
                )}
                {v.versions?.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setVersionsOf(v)} data-testid={`versions-btn-${v.slot}`}>
                    <History size={14} className="mr-1" /> v{v.version}
                  </Button>
                )}
              </div>
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-slate-500">Permitir descarga pública</span>
                <Switch checked={v.public_download} onCheckedChange={val => togglePublic(v.slot, val)}
                  data-testid={`public-download-switch-${v.slot}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="narration-card">
        <CardHeader>
          <CardTitle className="text-base">Narración con IA (voz en off)</CardTitle>
          <CardDescription className="text-xs">
            Genera la locución del guión institucional en español neutro con IA (OpenAI TTS HD) y descárgala como MP3 para tu productor audiovisual.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <select value={voice} onChange={e => setVoice(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm" data-testid="narration-voice-select">
            <option value="onyx">Onyx — voz grave y segura (recomendada)</option>
            <option value="nova">Nova — voz femenina enérgica</option>
            <option value="echo">Echo — voz calmada</option>
            <option value="sage">Sage — voz pausada</option>
            <option value="coral">Coral — voz cálida</option>
          </select>
          <Button disabled={generating} onClick={async () => {
            setGenerating(true);
            try {
              const { data: r } = await api.post('/videos/narration/generate', { voz: voice });
              toast.success(`${r.message} (${r.size_mb} MB)`);
              load();
            } catch (e) { toast.error(e.response?.data?.detail || 'Error al generar la narración'); }
            finally { setGenerating(false); }
          }} data-testid="generate-narration-btn">
            {generating ? <><Loader2 size={14} className="animate-spin mr-1" /> Generando voz...</> : <><Mic size={14} className="mr-1" /> Generar narración IA</>}
          </Button>
          {data.assets.narracion?.disponible && (
            <>
              <audio controls preload="none" className="h-9"
                src={`${API_URL}/api/videos/assets-preview/narracion?ts=${Date.now()}`}
                data-testid="narration-audio-preview" />
              <Button size="sm" variant="outline" onClick={() => downloadAsset('narracion', 'SimuClinic_Narracion_ES.mp3')}
                data-testid="download-narration-btn">
                <Download size={14} className="mr-1" /> Descargar MP3
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card data-testid="demo-voices-card">
        <CardHeader>
          <CardTitle className="text-base">Voces de la demo extendida (comparar)</CardTitle>
          <CardDescription className="text-xs">
            Narración sincronizada del guión institucional generada con voces Microsoft (las mismas de Luvvoice). Dalia es la voz activa en la demo y los videos MP4.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          {[['demo_narracion', 'Dalia — México (activa)', 'dalia'], ['demo_narracion_catalina', 'Catalina — Chile', 'catalina']].map(([key, label, id]) => (
            <div key={key} className="flex flex-col gap-2 border rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <Volume2 size={15} className="text-cyan-600" /> {label}
              </div>
              <audio controls preload="none" className="w-full h-9"
                src={`${API_URL}/api/videos/assets-preview/${key}`}
                data-testid={`demo-voice-audio-${id}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Recursos del video</CardTitle>
          <CardDescription className="text-xs">Subtítulos sincronizados, portada, thumbnail y guión de producción.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {[['srt', 'Subtítulos SRT', FileText], ['vtt', 'Subtítulos VTT', FileText],
            ['cover', 'Portada PNG', Image], ['thumbnail', 'Thumbnail JPG', Image],
            ['guion', 'Guión de producción', FileText]].map(([key, label, Icon]) => {
            const asset = data.assets[key];
            return (
              <Button key={key} size="sm" variant="outline" disabled={!asset?.disponible}
                onClick={() => downloadAsset(key, asset.filename)} data-testid={`asset-btn-${key}`}>
                <Icon size={14} className="mr-1" /> {label}
              </Button>
            );
          })}
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={v => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{preview?.nombre}</DialogTitle>
            <DialogDescription>Reproducción por streaming (HTTP Range)</DialogDescription>
          </DialogHeader>
          {preview && (
            <video controls autoPlay className="w-full rounded-lg bg-black" preload="metadata"
              src={`${API_URL}/api/videos/${preview.slot}/stream`} data-testid="video-preview-player" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!versionsOf} onOpenChange={v => !v && setVersionsOf(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Versiones — {versionsOf?.nombre}</DialogTitle>
            <DialogDescription>Restaura una versión anterior si es necesario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(versionsOf?.versions || []).map(ver => (
              <div key={ver.version} className="flex items-center justify-between border rounded p-2 text-sm">
                <span>v{ver.version} · {ver.size_mb} MB · {ver.fecha ? new Date(ver.fecha).toLocaleDateString('es-CL') : '—'} · {ver.usuario || '—'}</span>
                <Button size="sm" variant="outline" onClick={() => restore(versionsOf.slot, ver.version)}
                  data-testid={`restore-btn-${ver.version}`}>Restaurar</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
